import {useCallback, useEffect, useRef, useState} from 'react';

import {
  getRenderedImageMetrics,
  ImageMetrics
} from '../utils/polygonUtils';

export interface OverlayDimensions extends ImageMetrics {
  isReady: boolean;
}

const EMPTY_METRICS: OverlayDimensions = {
  width: 0,
  height: 0,
  offsetX: 0,
  offsetY: 0,
  isReady: false
};

export const useDocumentOverlayDimensions = (
  imageElement: HTMLImageElement | null
): OverlayDimensions => {
  const [metrics, setMetrics] = useState<OverlayDimensions>(EMPTY_METRICS);
  const rafIdRef = useRef<number | null>(null);

  const measure = useCallback(() => {
    if (!imageElement) {
      setMetrics(EMPTY_METRICS);

      return;
    }

    const next = getRenderedImageMetrics(imageElement);
    setMetrics({
      ...next,
      isReady: next.width > 0 && next.height > 0
    });
  }, [imageElement]);

  const scheduleMeasure = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      measure();
    });
  }, [measure]);

  useEffect(() => {
    if (!imageElement) {
      setMetrics(EMPTY_METRICS);

      return;
    }

    imageElement.addEventListener('load', scheduleMeasure);

    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(imageElement);

    if (imageElement.complete && imageElement.naturalWidth > 0) {
      scheduleMeasure();
    }

    return () => {
      imageElement.removeEventListener('load', scheduleMeasure);
      observer.disconnect();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [imageElement, scheduleMeasure]);

  return metrics;
};
